import { useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../api/client";
import { useAdminOrder, useAdminStatus } from "../../api/hooks";
import { NeedTelegram } from "../../components/NeedTelegram";
import { OrderContact, OrderLines, OrderProgress, StatusBadge } from "../../components/OrderParts";
import { formatDateTime, formatOrderNumber } from "../../lib/money";
import { hapticNotify, hasTelegramContext, openTelegramLink } from "../../lib/telegram";

const EVENT_LABELS: Record<string, string> = {
  created: "Заказ оформлен",
  status_confirmed: "Подтверждён",
  status_shipped: "Отправлен",
  status_delivered: "Доставлен",
  status_cancelled: "Отменён",
};

/**
 * Карточка заказа для менеджера: контакты, доставка, Telegram покупателя,
 * журнал и переходы статуса. Разрешённые переходы решает API (422 с
 * текстом), экран лишь показывает его.
 */
export function AdminOrderScreen() {
  const { orderNumber = "" } = useParams();
  const query = useAdminOrder(orderNumber);
  const mutation = useAdminStatus(orderNumber);
  const [carrier, setCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!hasTelegramContext()) return <NeedTelegram what="Админка" />;
  if (query.isPending) return <div className="state">Загружаем…</div>;
  if (query.isError || !query.data) return <div className="state">Заказ не найден или нет доступа.</div>;

  const { order, user, events } = query.data;

  const apply = async (update: Parameters<typeof mutation.mutateAsync>[0]) => {
    setError(null);
    try {
      await mutation.mutateAsync(update);
      hapticNotify("success");
    } catch (e) {
      hapticNotify("error");
      setError(e instanceof ApiError ? e.message : "Запрос не выполнен");
    }
  };

  const cancelButton = (
    <button type="button" className="button button--danger" disabled={mutation.isPending} onClick={() => apply({ status: "cancelled" })}>
      Отменить заказ
    </button>
  );

  return (
    <div className="screen screen--plain">
      <div className="screen__header">
        <h1 className="screen__title">{formatOrderNumber(order.orderNumber)}</h1>
        <StatusBadge status={order.status} />
      </div>
      <p className="screen__subtitle">{formatDateTime(order.placedAt)}</p>

      <OrderProgress status={order.status} />

      <div className="section">Покупатель</div>
      <OrderContact order={order} />
      {user && (
        <button
          type="button"
          className="button button--ghost button--sm"
          style={{ marginTop: 8 }}
          onClick={() => openTelegramLink(user.username ? `https://t.me/${user.username}` : `tg://user?id=${user.telegramId}`)}
        >
          Написать в Telegram{user.username ? ` (@${user.username})` : ""}
        </button>
      )}

      <div className="section">Состав</div>
      <OrderLines order={order} />

      {error && <div className="alert" style={{ marginTop: 14 }}>{error}</div>}

      {order.status === "new" && (
        <>
          <div className="section">Действия</div>
          <div className="stack">
            <button type="button" className="button" disabled={mutation.isPending} onClick={() => apply({ status: "confirmed" })}>
              Подтвердить заказ
            </button>
            {cancelButton}
          </div>
        </>
      )}

      {order.status === "confirmed" && (
        <>
          <div className="section">Отправка</div>
          <div className="field">
            <label className="field__label" htmlFor="carrier">Служба доставки</label>
            <input id="carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="СДЭК, Почта России…" />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="tracking">Трек-номер</label>
            <input id="tracking" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="trackingUrl">Ссылка отслеживания (необязательно)</label>
            <input id="trackingUrl" type="url" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} />
          </div>
          <div className="stack">
            <button
              type="button"
              className="button"
              disabled={mutation.isPending}
              onClick={() =>
                apply({
                  status: "shipped",
                  ...(carrier.trim() ? { carrier: carrier.trim() } : {}),
                  ...(trackingNumber.trim() ? { trackingNumber: trackingNumber.trim() } : {}),
                  ...(trackingUrl.trim() ? { trackingUrl: trackingUrl.trim() } : {}),
                })
              }
            >
              Отметить отправленным
            </button>
            {cancelButton}
          </div>
        </>
      )}

      {order.status === "shipped" && (
        <>
          <div className="section">Отправление</div>
          <div className="small">
            {order.shipment.carrier && <div>{order.shipment.carrier}</div>}
            {order.shipment.trackingNumber && <div>Трек-номер: {order.shipment.trackingNumber}</div>}
          </div>
          <div className="stack" style={{ marginTop: 12 }}>
            <button type="button" className="button" disabled={mutation.isPending} onClick={() => apply({ status: "delivered" })}>
              Отметить доставленным
            </button>
            {cancelButton}
          </div>
        </>
      )}

      <div className="section">Журнал</div>
      {events.map((event) => (
        <div key={`${event.type}-${event.createdAt}`} className="row" style={{ padding: 10 }}>
          <div className="row__body">
            <div className="row__title" style={{ fontSize: 13 }}>
              {EVENT_LABELS[event.type] ?? event.type} · {event.actor}
            </div>
            {event.note && <div className="row__meta">{event.note}</div>}
            <div className="row__meta">{formatDateTime(event.createdAt)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
